import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Select } from '@actual-app/components/select';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import type { TFunction } from 'i18next';

import { Setting } from './UI';

const CLAVE = 'presupuesto:escala';
// Valor por defecto si el usuario no ha elegido (debe coincidir con public/escala.js).
export const ESCALA_POR_DEFECTO = '0.8';

/** Escalas ofrecidas (como se guardan en localStorage). */
export const ESCALAS = ['0.6', '0.7', '0.8', '0.9', '1'] as const;

/**
 * Nombre comprensible de cada escala (H03), sin jerga técnica. El 80 % (el
 * valor por defecto) es «Normal»; «Muy grande» es el 100 %, sin reducir.
 */
export function nombreEscala(t: TFunction, valor: string): string {
  const pct = `${Math.round(Number(valor) * 100)} %`;
  switch (valor) {
    case '0.6':
      return `${t('Very compact')} (${pct})`;
    case '0.7':
      return `${t('Compact')} (${pct})`;
    case '0.8':
      return `${t('Normal')} (${pct})`;
    case '0.9':
      return `${t('Large')} (${pct})`;
    case '1':
      return `${t('Extra large')} (${pct})`;
    default:
      return pct;
  }
}

/** Escala guardada (o la de por defecto). */
export function escalaElegida(): string {
  try {
    return localStorage.getItem(CLAVE) || ESCALA_POR_DEFECTO;
  } catch {
    return ESCALA_POR_DEFECTO;
  }
}

/** Escala que de verdad se aplica ahora (1 si public/escala.js no actuó). */
function escalaAplicada(): number {
  const w = window as typeof window & { __presupuestoEscala?: number };
  return w.__presupuestoEscala ?? 1;
}

/** Tamaño de la interfaz en el móvil (escala del viewport, ver escala.js). */
export function EscalaSettings() {
  const { t, i18n } = useTranslation();
  const [guardada] = useState(escalaElegida);
  const [elegida, setElegida] = useState(guardada);

  const valores: string[] = [...ESCALAS];
  // Un valor guardado que no esté en la lista se conserva tal cual.
  if (!valores.includes(guardada)) {
    valores.push(guardada);
  }
  const opciones = valores.map(
    v => [v, nombreEscala(t, v)] as [string, string],
  );

  // La muestra se dibuja a la escala elegida relativa a la que ya se ve: así
  // enseña cómo quedará la pantalla tras aplicar.
  const k = Number(elegida) / escalaAplicada();
  let importe = '-45,20 €';
  try {
    importe = new Intl.NumberFormat(i18n.language || 'es-ES', {
      style: 'currency',
      currency: 'EUR',
    }).format(-45.2);
  } catch {
    // idioma no reconocido: se queda el texto fijo
  }

  const cambiada = elegida !== guardada;
  const aplicar = () => {
    try {
      localStorage.setItem(CLAVE, elegida);
    } catch {
      // sin almacenamiento: no se recuerda
    }
    window.location.reload();
  };

  return (
    <Setting
      primaryAction={
        <View
          data-testid="escala-selector"
          style={{ flexDirection: 'row', gap: 10, alignItems: 'center' }}
        >
          <Select
            aria-label={t('Interface size')}
            options={opciones}
            value={elegida}
            onChange={setElegida}
          />
          {cambiada && (
            <Button variant="primary" onPress={aplicar}>
              <Trans>Apply</Trans>
            </Button>
          )}
        </View>
      }
    >
      <Text>
        <Trans>
          <strong>Interface size</strong> on the phone: everything (text,
          spacing, rows) scales together. Saved on this device.
        </Trans>
      </Text>
      <View
        data-testid="escala-muestra"
        aria-label={t('Preview')}
        style={{
          marginTop: 8,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10 * k,
          maxWidth: 360,
          padding: `${10 * k}px ${14 * k}px`,
          borderRadius: 12 * k,
          border: `1px solid ${theme.tableBorder}`,
          backgroundColor: theme.tableBackground,
          fontSize: 15 * k,
        }}
      >
        <View
          style={{ flexDirection: 'row', alignItems: 'center', gap: 10 * k }}
        >
          <span
            aria-hidden
            style={{
              width: 30 * k,
              height: 30 * k,
              borderRadius: '50%',
              backgroundColor: theme.pillBackgroundSelected,
              flexShrink: 0,
            }}
          />
          <View>
            <Text style={{ fontWeight: 700 }}>
              <Trans>Supermarket</Trans>
            </Text>
            <Text style={{ fontSize: 12 * k, color: theme.pageTextSubdued }}>
              <Trans>Groceries</Trans>
            </Text>
          </View>
        </View>
        <Text style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
          {importe}
        </Text>
      </View>
      {cambiada && (
        <Text
          style={{ fontSize: 12, color: theme.pageTextSubdued, marginTop: 6 }}
        >
          <Trans>Press "Apply" to use this size. The page will reload.</Trans>
        </Text>
      )}
    </Setting>
  );
}
